import {
  Candidate,
  DataLevel,
  GradientRow,
  Result,
  StructureType,
} from '@/lib/model';
import * as React from 'react';
import { generateGradientTable } from './candidatesHelpers';
import { getLastVersion } from './getResults';
import { parsePercentage } from './helpers';
import { usePrevious } from './hookHelpers';

export interface MainContextType {
  structureType: StructureType;
  setStructureType: (type: StructureType) => void;
  dataLevel: DataLevel;
  setDataLevel: (level: DataLevel) => void;
  results: Result[];
  setResults: (results: Result[]) => void;
  selectedResults: Result[];
  handleResult: (results: Result | Result[], isWithinList: boolean) => void;
  nationalResult: Result | null;
  setNationalResult: (result: Result) => void;
  lastResult: Result;
  lastCollegio?: Result;
  isComuneLoading: boolean;
  setComuneIsLoading: (value: boolean) => void;
  selectedCandidate: Candidate | null;
  setSelectedCandidate: (candidate?: Candidate) => void;
  gradientTable: GradientRow[];
  innerCandidatesList: Candidate[];
  lastVersion: number;
  isTurnoutOpen: boolean;
  toggleTurnout: () => void;
  is2018: boolean;
  setIs2018: (value: boolean) => void;
}

const INITIAL_STATE = {
  structureType: StructureType.CAMERA,
  setStructureType: () => {},
  dataLevel: DataLevel.CIRCOSCRIZIONE,
  setDataLevel: () => {},
  results: [],
  setResults: () => {},
  selectedResults: [],
  handleResult: () => {},
  nationalResult: {} as Result,
  setNationalResult: () => {},
  lastResult: {} as Result,
  lastCollegio: {} as Result,
  isComuneLoading: false,
  setComuneIsLoading: () => {},
  selectedCandidate: {} as Candidate,
  setSelectedCandidate: () => {},
  gradientTable: [],
  innerCandidatesList: [],
  lastVersion: 1,
  isTurnoutOpen: false,
  toggleTurnout: () => {},
  is2018: false,
  setIs2018: () => {},
};

const MainContext = React.createContext<MainContextType>(INITIAL_STATE);

export const useMainContext = () => React.useContext(MainContext);

export const MainProvider: React.FC<Partial<React.PropsWithChildren>> = ({
  children,
}) => {
  const [structureType, setStructureType] = React.useState<StructureType>(
    INITIAL_STATE.structureType
  );
  const [dataLevel, setDataLevelContext] = React.useState<DataLevel>(
    INITIAL_STATE.dataLevel
  );
  const [results, setResults] = React.useState<Result[]>([]);
  const [selectedResults, setSelectedResults] = React.useState<Result[]>([]);
  const [nationalResult, setNationalResult] = React.useState<Result | null>(
    null
  );
  const [isComuneLoading, setComuneIsLoading] = React.useState(
    INITIAL_STATE.isComuneLoading
  );
  const [selectedCandidate, setSelectedCandidateContext] =
    React.useState<Candidate | null>(null);
  const prevStructureType = usePrevious<StructureType>(
    structureType,
    StructureType.CAMERA
  );
  const [isTurnoutOpen, setIsTurnoutOpen] = React.useState(
    INITIAL_STATE.isTurnoutOpen
  );
  const [lastVersion, setLastVersion] = React.useState(
    INITIAL_STATE.lastVersion
  );
  const [is2018, setIs2018] = React.useState(INITIAL_STATE.is2018);
  const timeOut = React.useRef<NodeJS.Timeout>();

  const lastResult = selectedResults[selectedResults.length - 1] as Result;
  const lastCollegio = selectedResults.find(
    r => r.level === DataLevel.COLLEGIO
  );

  React.useEffect(() => {
    const fn = async () => {
      clearTimeout(timeOut.current);

      const version = await getLastVersion();
      if (version !== lastVersion) {
        setLastVersion(version);
      }

      const MINUTE = 1000 * 60;
      timeOut.current = setTimeout(fn, MINUTE);
    };

    fn();

    return () => {
      clearTimeout(timeOut.current);
    };
  }, [lastVersion]);

  const setDataLevel = React.useCallback(
    (level: DataLevel) => {
      if (selectedCandidate) {
        setSelectedCandidate();
      }

      setDataLevelContext(level);
    },
    [selectedCandidate]
  );

  // set national result
  React.useEffect(() => {
    if (!nationalResult) {
      return;
    }

    setSelectedResults(prevResults => [
      nationalResult,
      ...prevResults.slice(1),
    ]);
  }, [nationalResult]);

  // structure type change
  React.useEffect(() => {
    if (structureType === prevStructureType) {
      return;
    }

    setSelectedResults(prevResults => {
      const firstResult = prevResults[0];
      if (!firstResult) {
        return [];
      }

      return [firstResult];
    });

    setDataLevel(INITIAL_STATE.dataLevel);
  }, [structureType, prevStructureType, setDataLevel]);

  // Adding a new selected result
  const handleResult = (results: Result | Result[], isWithinList: boolean) => {
    if (!Array.isArray(results)) {
      results = [results];
    }

    const firstResultFromList = results[0] as Result;
    const lastResultFromList = results[results.length - 1] as Result;

    if (
      lastResultFromList.level === DataLevel.NAZIONALE &&
      dataLevel === DataLevel.COMUNE
    ) {
      setDataLevel(INITIAL_STATE.dataLevel);
    } else if (lastResultFromList.level === DataLevel.CIRCOSCRIZIONE) {
      setDataLevel(DataLevel.COLLEGIO);
    } else if (lastResultFromList.level === DataLevel.COLLEGIO) {
      setDataLevel(DataLevel.COMUNE);
    }

    setSelectedResults(prevResults => {
      if (isWithinList) {
        return prevResults.filter(r => r.level <= firstResultFromList.level);
      }

      return [
        ...prevResults.filter(r => r.level < firstResultFromList.level),
        ...(results as Result[]),
      ];
    });
  };

  // Choose an inner candidate
  const setSelectedCandidate = (candidate?: Candidate) => {
    if (isTurnoutOpen) {
      setIsTurnoutOpen(false);
    }

    setSelectedCandidateContext(() => {
      if (!candidate) {
        return null;
      }

      return candidate;
    });
  };

  // Filter candidates list to chosen candidate
  const innerCandidatesList = React.useMemo(() => {
    if (!selectedCandidate) {
      return [];
    }

    return results
      .reduce((acc: Candidate[], result: Result) => {
        const foundCoalition = result.coalitions.find(
          coalition => coalition.coalitionId === selectedCandidate.coalitionId
        );

        if (!foundCoalition) {
          return acc;
        }

        const { candidates, ...coalition } = foundCoalition;
        let valueToReturn: Candidate = coalition;

        if (selectedCandidate.candidateId) {
          const candidate = candidates.find(
            candidate => candidate.candidateId === selectedCandidate.candidateId
          ) as Candidate;

          valueToReturn = candidate;
        }

        valueToReturn.label = result.placeName;

        return [...acc, valueToReturn];
      }, [])
      .sort(({ percentage: a }, { percentage: b }) => b - a);
  }, [selectedCandidate, results]);

  // Generate colors table
  const gradientTable = React.useMemo(() => {
    if (isTurnoutOpen) {
      return generateGradientTable(
        results.map(r => r.summary.turnout),
        'value'
      );
    }

    return generateGradientTable(innerCandidatesList, 'percentage');
  }, [innerCandidatesList, isTurnoutOpen, results]);

  const resultsWithSelectedCandidate = React.useMemo(() => {
    if (!selectedCandidate && !isTurnoutOpen) {
      return results;
    }

    return results.reduce((acc: Result[], result) => {
      let fillColor: string = result.mapLayer.fillColor;
      let valueToConsider = parsePercentage(result.summary.turnout.value);
      if (!isTurnoutOpen) {
        const innerCandidate = innerCandidatesList.find(
          c => c.label === result.placeName
        );

        valueToConsider = innerCandidate?.percentage || 0;

        if (selectedCandidate) {
          fillColor = selectedCandidate?.fillColor;
        }
      } else {
        // state color for affluenza
        fillColor = '#a9a9a9';
      }

      const { opacity } =
        gradientTable.find(row => valueToConsider <= row.value) ||
        gradientTable[0]; // if no result has been found, use first gradient.

      return [
        ...acc,
        {
          ...result,
          mapLayer: {
            ...result.mapLayer,
            fillColor,
            opacity,
          },
        },
      ];
    }, []);
  }, [
    selectedCandidate,
    innerCandidatesList,
    gradientTable,
    results,
    isTurnoutOpen,
  ]);

  const toggleTurnout = () => {
    if (selectedCandidate) {
      setSelectedCandidate();
    }

    setIsTurnoutOpen(prevTurnout => !prevTurnout);
  };

  const toggleIs2018 = (value: boolean) => {
    handleResult(nationalResult as Result, true);
    setStructureType(INITIAL_STATE.structureType);
    if (dataLevel === DataLevel.COMUNE) {
      setDataLevel(INITIAL_STATE.dataLevel);
    }

    setIs2018(value);
  };

  return (
    <MainContext.Provider
      value={{
        structureType,
        setStructureType,
        dataLevel,
        setDataLevel,
        results: resultsWithSelectedCandidate,
        setResults,
        selectedResults,
        handleResult,
        nationalResult,
        setNationalResult,
        lastResult,
        lastCollegio,
        isComuneLoading,
        setComuneIsLoading,
        selectedCandidate,
        setSelectedCandidate,
        gradientTable,
        innerCandidatesList,
        lastVersion,
        isTurnoutOpen,
        toggleTurnout,
        is2018,
        setIs2018: toggleIs2018,
      }}
    >
      {children}
    </MainContext.Provider>
  );
};
